import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { TWModal } from '../../../components/ui';
import { cn } from '../../../components/ui/_cn';
import publicCouponApi from '../../../services/publicCouponApi';
import storeApi from '../../../services/storeApi';
import menuApi from '../../../services/menuApi';
import { apiMessage } from '../../connect/connect-api-message';
import FinButton from '../../connect/finance/fin-button';
import RuleSentence from './campaign/RuleSentence';
import CodeField, { useCodeCheck } from './campaign/CodeField';
import AdvancedSettings from './campaign/AdvancedSettings';
import ConfirmDialog, { NEW_DISCARD_COPY } from './campaign/ConfirmDialog';
import { menusForStore } from './coupon-menus';
import {
  ALL_STORES,
  CODE_FORMAT_MESSAGES,
  MESSAGES,
  codeFormatError,
  emptyRule,
  generateCode,
  isTakenMessage,
  itemOf,
  pickStore,
  storeChoices,
  summarySentence,
  validateRule,
} from '../lib/campaign-rule';
import { CHANNEL_ORDER, upsertBody } from '../lib/campaign-body';
import { CAMPAIGN_STATUS } from '../lib/campaign-status';

// The three-step New campaign modal (spec 2026-10-21 §2.4, handoff §2–§4):
// 520px wide, 455px tall (it grows only when Advanced settings opens on
// step 3), three 20×4 progress bars, a close × on every step. Step 1 picks
// the type, step 2 is the rule as a sentence, step 3 shows the name and code
// written from it (each stops following the rule once edited by hand) and
// the Advanced settings. Below sm it is TWModal's full-width sheet.
export const NEW_COPY = {
  title: 'New campaign',
  typeHeading: 'Select your campaign type',
  typeGroup: 'Campaign type',
  ready: 'Ready to create',
  name: 'Campaign name',
  back: 'Back',
  continue: 'Continue',
  draft: 'Save as draft',
  create: 'Create campaign',
  close: 'Close',
  saveFailed: 'Couldn’t save the campaign. Try again.',
};

const PizzaGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
    <path d="M12 21 3.5 5.5a16 16 0 0 1 17 0L12 21Z" />
    <path d="M5.2 8.4a13 13 0 0 1 13.6 0" />
    <circle cx="10" cy="11.5" r="1" />
    <circle cx="13.8" cy="14" r="1" />
  </svg>
);

// Handoff §2 step 1: title, example line, icon tile.
const TYPE_CARDS = [
  { type: 'discount', title: 'A discount', example: 'Spend $20, get 10% off', glyph: <span className="text-[17px] font-bold">%</span> },
  { type: 'free_item', title: 'A free item', example: 'Spend $10, get a free Coke', glyph: <PizzaGlyph /> },
  { type: 'buy_x_get_y', title: 'Extra item free', example: 'Buy 1 fries, get 1 free', glyph: <span className="text-[13px] font-bold">1+1</span> },
];

const Progress = ({ step }) => (
  <div role="progressbar" aria-label={`Step ${step} of 3`} aria-valuemin={1} aria-valuemax={3} aria-valuenow={step} className="flex gap-1.5">
    {[1, 2, 3].map((n) => (
      <span key={n} className={cn('h-1 w-5 rounded-full', n <= step ? 'bg-fin-blue' : 'bg-fin-border')} />
    ))}
  </div>
);

const BackButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 rounded-md px-1 py-1.5 text-[13.5px] font-semibold text-fin-muted hover:text-fin-ink"
  >
    <ChevronLeftIcon className="h-3.5 w-3.5 stroke-[2.5]" aria-hidden="true" />
    {NEW_COPY.back}
  </button>
);

const NewCampaignModal = ({ open, companyId, onClose, onCreated, onOpenOwner }) => {
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [rule, setRule] = useState(null);
  const [name, setName] = useState('');
  const [nameDirty, setNameDirty] = useState(false);
  const [code, setCode] = useState('');
  const [codeDirty, setCodeDirty] = useState(false);
  const [storeid, setStoreid] = useState(null);
  // Only the Advanced Store row chooses a store; the narrowing's pick is a default.
  const [storeChosen, setStoreChosen] = useState(false);
  const [platforms, setPlatforms] = useState(CHANNEL_ORDER);
  const [menuids, setMenuids] = useState([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [edited, setEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [footerError, setFooterError] = useState('');
  const [serverCodeError, setServerCodeError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [stores, setStores] = useState([]);
  const [menus, setMenus] = useState([]);
  const [choices, setChoices] = useState([]);
  const [choicesState, setChoicesState] = useState('loading');
  const nameErrorId = useId();

  // ProductChoices, once per modal (spec §2.4); Retry reads it again. An
  // answer applies only if it is the latest request: closing or switching
  // company (the effect's cleanup) retires the one in flight, so company A's
  // products never reach B's picker.
  const choicesRequest = useRef(0);
  const loadChoices = useCallback(async () => {
    const id = ++choicesRequest.current;
    setChoicesState('loading');
    try {
      const res = await publicCouponApi.productChoices(companyId);
      if (id !== choicesRequest.current) return;
      setChoices(res?.data?.data || []);
      setChoicesState('ready');
    } catch {
      if (id !== choicesRequest.current) return;
      setChoices([]);
      setChoicesState('failed');
    }
  }, [companyId]);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setStep(1);
    setType(null);
    setRule(null);
    setName('');
    setNameDirty(false);
    setCode('');
    setCodeDirty(false);
    setStoreid(null);
    setStoreChosen(false);
    setPlatforms([...CHANNEL_ORDER]);
    setMenuids([]);
    setAdvancedOpen(false);
    setEdited(false);
    setSaving(false);
    setFooterError('');
    setServerCodeError(null);
    setConfirm(null);
    storeApi
      .list(`companyid=${companyId}`)
      .then((res) => {
        if (live) setStores(res?.data?.data || []);
      })
      .catch(() => {
        if (live) setStores([]);
      });
    menuApi
      .list(`?companyid=${companyId}`)
      .then((res) => {
        if (live) setMenus(res?.data?.data || []);
      })
      .catch(() => {
        if (live) setMenus([]);
      });
    loadChoices();
    return () => {
      live = false;
      choicesRequest.current += 1;
    };
  }, [open, companyId, loadChoices]);

  const product = rule?.productId ? choices.find((choice) => choice.productid === rule.productId) || null : null;
  const item = itemOf(product, rule?.itemId);
  // A free item is an Item: a product without one cannot be the reward.
  const typeProducts = useMemo(
    () => (type === 'free_item' ? choices.filter((choice) => (choice.items || []).length > 0) : choices),
    [type, choices],
  );
  const ruleErrors = rule ? validateRule(rule, product) : {};
  const ruleValid = Boolean(rule) && Object.keys(ruleErrors).length === 0;
  // Step 2 names a problem only for a value the reader has typed; an empty
  // field just keeps Continue off.
  const shownRuleErrors = rule
    ? Object.fromEntries(Object.entries(ruleErrors).filter(([field]) => rule[field] !== null && rule[field] !== undefined))
    : {};

  // Handoff §3: name and code follow the rule until edited by hand.
  const generatedName = ruleValid ? summarySentence(rule, product, item) : '';
  const generatedCode = ruleValid ? generateCode(rule, product) : '';
  useEffect(() => {
    if (!nameDirty && generatedName) setName(generatedName);
  }, [generatedName, nameDirty]);
  useEffect(() => {
    if (!codeDirty && generatedCode) setCode(generatedCode);
  }, [generatedCode, codeDirty]);

  const formatError = codeDirty ? codeFormatError(code) : null;
  const { answer, check } = useCodeCheck({ companyId, code, enabled: open && step === 3 && Boolean(code) && !formatError });

  // A generated code that is taken takes the api's suggestion and stays
  // automatic (spec §2.4).
  useEffect(() => {
    if (codeDirty || !answer || answer.available || !answer.suggestion) return;
    setCode(answer.suggestion);
  }, [answer, codeDirty]);

  const owner = answer && !answer.available && answer.usedby && (codeDirty || !answer.suggestion) ? answer.usedby : null;
  const answerError = answer && !answer.available && !answer.usedby ? answer.message : null;
  const serverError = serverCodeError && serverCodeError.code === code ? serverCodeError.message : null;
  const codeError = formatError || answerError || serverError;
  const codeBlocked = Boolean(codeError || owner);

  // D3: a free item's store is one that sells it; the first selling store
  // when not every store does.
  const storeOptions = useMemo(() => storeChoices(type, product, stores), [type, product, stores]);
  // A store nobody chose is a default (handoff §2: all stores when
  // untouched): it goes back to All stores whenever that is offered.
  useEffect(() => {
    setStoreid((current) =>
      !storeChosen && storeOptions.some((option) => option.value === ALL_STORES) ? null : pickStore(storeOptions, current),
    );
  }, [storeOptions, storeChosen]);

  // Menus narrowed to the store, as today (coupon-menus.js): a menu the new
  // store does not carry is dropped; one the list does not know is kept.
  useEffect(() => {
    const visible = menusForStore(menus, storeid);
    setMenuids((prev) => {
      const kept = prev.filter((id) => !menus.some((menu) => menu.id === id) || visible.some((menu) => menu.id === id));
      return kept.length === prev.length ? prev : kept;
    });
  }, [menus, storeid]);

  const showStore = stores.length > 1;
  const storeLabel = storeOptions.find((option) => option.value === (storeid ?? ALL_STORES))?.label || '';
  const nameError = nameDirty && !name.trim() ? MESSAGES.name : null;
  const canSave = ruleValid && Boolean(name.trim()) && Boolean(code) && !codeBlocked && !saving;

  const markEdited = () => {
    setEdited(true);
    setFooterError('');
  };
  const changeRule = (patch) => {
    setRule((prev) => ({ ...prev, ...patch }));
    markEdited();
  };
  const continueFromType = () => {
    if (!rule || rule.type !== type) {
      setRule(emptyRule(type));
      setName('');
      setNameDirty(false);
      setCode('');
      setCodeDirty(false);
      setServerCodeError(null);
    }
    setStep(2);
  };
  const changeName = (value) => {
    setName(value);
    setNameDirty(true);
    markEdited();
  };
  const commitCode = (value) => {
    setCode(value);
    setCodeDirty(true);
    setServerCodeError(null);
    markEdited();
  };

  // Closing discards at once unless something was entered (handoff §2).
  const requestClose = () => {
    if (saving) return;
    if (edited) setConfirm({ owner: null });
    else onClose();
  };
  // The taken code's name opens that coupon once the discard is confirmed.
  const openOwnerLink = (target) => setConfirm({ owner: target });
  const discard = () => {
    const target = confirm?.owner;
    setConfirm(null);
    onClose();
    if (target) onOpenOwner(target, code);
  };

  const save = async (status) => {
    if (!canSave) return;
    setSaving(true);
    setFooterError('');
    setServerCodeError(null);
    try {
      await publicCouponApi.upsert(
        upsertBody(
          { name: name.trim(), code, rule, storeid, platforms, menuids, status, startsat: null, endsat: null },
          { companyid: companyId, id: null },
        ),
      );
      setSaving(false);
      onCreated();
      onClose();
    } catch (error) {
      setSaving(false);
      const text = apiMessage(error);
      if (isTakenMessage(text) || CODE_FORMAT_MESSAGES.includes(text)) {
        // The api's refusal is authoritative (Review Focus 5): its sentence
        // stands at once; CodeAvailability, when it answers, adds the link
        // (or, for a generated code, its suggestion).
        setServerCodeError({ code, message: text });
        if (isTakenMessage(text)) check(code);
      } else {
        setFooterError(text || NEW_COPY.saveFailed);
      }
    }
  };

  let content;
  if (step === 1) {
    content = (
      <>
        <h2 className="text-[20px] font-bold tracking-[-0.01em] text-fin-ink">{NEW_COPY.typeHeading}</h2>
        <div role="radiogroup" aria-label={NEW_COPY.typeGroup} className="mt-5 space-y-2.5">
          {TYPE_CARDS.map((card) => {
            const selected = type === card.type;
            return (
              <button
                key={card.type}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => setType(card.type)}
                className={cn(
                  'flex w-full items-center gap-3.5 rounded-xl border px-4 py-3.5 text-left',
                  selected ? 'border-fin-blue shadow-[0_0_0_3px_rgb(var(--fin-blue)_/_0.12)]' : 'border-fin-border hover:bg-fin-hover',
                )}
              >
                <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-fin-selected text-fin-ink-2">
                  {card.glyph}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[14.5px] font-bold text-fin-ink">{card.title}</span>
                  <span className="block text-[13px] text-fin-muted">{card.example}</span>
                </span>
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border',
                    selected ? 'border-fin-blue bg-fin-blue text-white' : 'border-fin-border-2',
                  )}
                >
                  {selected ? <CheckIcon className="h-3 w-3 stroke-[3]" /> : null}
                </span>
              </button>
            );
          })}
        </div>
      </>
    );
  } else if (step === 2) {
    content = (
      <div className="flex flex-1 items-center">
        <RuleSentence
          rule={rule}
          onChange={changeRule}
          products={typeProducts}
          productsState={choicesState}
          onRetryProducts={loadChoices}
          errors={shownRuleErrors}
        />
      </div>
    );
  } else {
    content = (
      <div className="space-y-4">
        <div>
          <p className="text-[12.5px] font-medium text-fin-muted">{NEW_COPY.ready}</p>
          <h2 className="mt-1 text-[22px] font-bold leading-tight tracking-[-0.01em] text-fin-ink">{generatedName}</h2>
        </div>
        {/* The error sits outside the label, so the field's name stays
            "Campaign name" while it shows. */}
        <div className="space-y-1.5">
          <label className="block space-y-1.5">
            <span className="block text-[13px] font-semibold text-fin-ink">{NEW_COPY.name}</span>
            <input
              type="text"
              value={name}
              aria-invalid={nameError ? true : undefined}
              aria-describedby={nameError ? nameErrorId : undefined}
              onChange={(event) => changeName(event.target.value)}
              className={cn(
                'h-10 w-full rounded-lg border bg-fin-surface px-3 text-[13.5px] text-fin-ink outline-none focus:border-fin-blue',
                nameError ? 'border-fin-red' : 'border-fin-border-2',
              )}
            />
          </label>
          {nameError ? (
            <p id={nameErrorId} className="text-[12.5px] text-fin-red">
              {nameError}
            </p>
          ) : null}
        </div>
        <CodeField value={code} onCommit={commitCode} error={codeError} owner={owner} onOpenOwner={openOwnerLink} />
        <AdvancedSettings
          open={advancedOpen}
          onToggle={() => setAdvancedOpen((prev) => !prev)}
          showStore={showStore}
          storeOptions={storeOptions}
          storeid={storeid}
          onStore={(value) => {
            setStoreid(value);
            setStoreChosen(true);
            markEdited();
          }}
          platforms={platforms}
          onPlatforms={(value) => {
            setPlatforms(value);
            markEdited();
          }}
          menus={menusForStore(menus, storeid)}
          menuids={menuids}
          onMenus={(value) => {
            setMenuids(value);
            markEdited();
          }}
          collapsedNote={showStore ? storeLabel : ''}
        />
      </div>
    );
  }

  let footer;
  if (step === 1) {
    footer = (
      <div className="flex justify-end">
        <FinButton variant="primary" disabled={!type} onClick={continueFromType}>
          {NEW_COPY.continue}
          <ChevronRightIcon className="ml-1 h-3.5 w-3.5 stroke-[2.5]" aria-hidden="true" />
        </FinButton>
      </div>
    );
  } else if (step === 2) {
    footer = (
      <div className="flex items-center justify-between gap-2">
        <BackButton onClick={() => setStep(1)} />
        <FinButton variant="primary" disabled={!ruleValid} onClick={() => setStep(3)}>
          {NEW_COPY.continue}
          <ChevronRightIcon className="ml-1 h-3.5 w-3.5 stroke-[2.5]" aria-hidden="true" />
        </FinButton>
      </div>
    );
  } else {
    footer = (
      <div className="flex flex-wrap items-center justify-between gap-2">
        <BackButton onClick={() => setStep(2)} />
        <div className="flex flex-wrap items-center gap-2">
          <FinButton disabled={!canSave} onClick={() => save(CAMPAIGN_STATUS.draft)}>
            {NEW_COPY.draft}
          </FinButton>
          <FinButton variant="primary" disabled={!canSave} onClick={() => save(CAMPAIGN_STATUS.active)}>
            {NEW_COPY.create}
          </FinButton>
        </div>
      </div>
    );
  }

  return (
    <TWModal
      open={open}
      onClose={requestClose}
      size="lg"
      // The panel's own overflow-hidden would clip FinDropdown's menus
      // (payout-schedule-modal.js); `!` wins over TWModal's sm:max-w-lg and
      // px-5 py-4, which `cn` (a plain join) cannot replace.
      panelClassName="overflow-visible sm:!max-w-[520px]"
      bodyClassName="!p-0"
    >
      <div className="flex flex-col rounded-t-2xl bg-fin-surface sm:min-h-[455px] sm:rounded-md">
        <Dialog.Title as="h2" className="sr-only">
          {NEW_COPY.title}
        </Dialog.Title>
        <div className="flex items-center justify-between px-6 pt-6">
          <Progress step={step} />
          <button
            type="button"
            aria-label={NEW_COPY.close}
            onClick={requestClose}
            className="-m-1 rounded-md p-1 text-fin-muted hover:bg-fin-neutral-pale hover:text-fin-ink"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="flex flex-1 flex-col px-6 pb-6 pt-5">{content}</div>
        <div className="mt-auto border-t border-fin-border bg-fin-recess px-6 py-4 sm:rounded-b-md">
          {footer}
          {footerError ? (
            <p role="alert" className="mt-2 text-[13px] text-fin-red">
              {footerError}
            </p>
          ) : null}
        </div>
      </div>
      <ConfirmDialog
        open={Boolean(confirm)}
        title={NEW_DISCARD_COPY.title}
        body={NEW_DISCARD_COPY.body}
        confirmLabel={NEW_DISCARD_COPY.confirm}
        cancelLabel={NEW_DISCARD_COPY.cancel}
        danger
        onConfirm={discard}
        onCancel={() => setConfirm(null)}
      />
    </TWModal>
  );
};

export default NewCampaignModal;
